/**
 * Point-symbol geometry shared by the MapLibre and OpenLayers vector paths.
 *
 * The Vector ToolCard offers twelve point symbols, but only five of them are
 * true geometric primitives (circle / square / triangle / star / diamond).
 * The seven icon-style entries (pin, flag, home, ...) resolve to the nearest
 * primitive plus a base rotation, so the picker never emits a symbol an engine
 * cannot draw. Because twelve ids collapse onto five shapes, some entries do
 * render identically — `pointSymbolRenderLabel` exists so the panel can tell
 * the user what they will actually get.
 *
 * Both engines consume the same descriptors:
 *   - OpenLayers builds `ol/style/RegularShape` (or `Circle`) from
 *     {@link regularShapeParamsFor}.
 *   - MapLibre has no shape primitive for points, so it rasterizes the same
 *     geometry through {@link rasterizePointSymbol} and uses a `symbol` layer.
 */
import { isPointIconId, peekPointIcon } from './pointIconRegistry';
import type { BuiltInPointSymbolId } from './vectorStyleModel';

export type PointSymbolShape = 'circle' | 'square' | 'triangle' | 'star' | 'diamond';

export interface PointSymbolGeometry {
  readonly shape: PointSymbolShape;
  /**
   * Degrees folded into the user's rotation. Lets several ids share one
   * primitive while still reading differently (a "pin" is a triangle whose
   * apex points down, a "flag" points right).
   */
  readonly baseRotationDeg: number;
}

const SYMBOL_GEOMETRY: Readonly<Record<BuiltInPointSymbolId, PointSymbolGeometry>> = {
  circle: { shape: 'circle', baseRotationDeg: 0 },
  square: { shape: 'square', baseRotationDeg: 0 },
  triangle: { shape: 'triangle', baseRotationDeg: 0 },
  star: { shape: 'star', baseRotationDeg: 0 },
  diamond: { shape: 'diamond', baseRotationDeg: 0 },
  pin: { shape: 'triangle', baseRotationDeg: 180 },
  marker: { shape: 'triangle', baseRotationDeg: 90 },
  flag: { shape: 'triangle', baseRotationDeg: 270 },
  home: { shape: 'square', baseRotationDeg: 0 },
  car: { shape: 'square', baseRotationDeg: 0 },
  checkpoint: { shape: 'circle', baseRotationDeg: 0 },
  destination: { shape: 'star', baseRotationDeg: 36 },
};

const SHAPE_LABELS: Readonly<Record<PointSymbolShape, string>> = {
  circle: 'Circle',
  square: 'Square',
  triangle: 'Triangle',
  star: 'Star',
  diamond: 'Diamond',
};

export function pointSymbolGeometryFor(id: string): PointSymbolGeometry {
  return SYMBOL_GEOMETRY[id as BuiltInPointSymbolId] ?? SYMBOL_GEOMETRY.circle;
}

/**
 * Human-readable description of what an id will draw: the icon name for a
 * registry symbol, otherwise the primitive plus any base rotation.
 */
export function pointSymbolRenderLabel(id: string): string {
  if (isPointIconId(id)) {
    const entry = peekPointIcon(id);
    return entry ? `${entry.label} (${entry.setLabel} icon)` : id;
  }
  const { shape, baseRotationDeg } = pointSymbolGeometryFor(id);
  const label = SHAPE_LABELS[shape];
  return baseRotationDeg === 0 ? label : `${label} rotated ${baseRotationDeg}°`;
}

/**
 * Total on-screen rotation for a symbol: the id's base offset plus whatever
 * the user picked. Normalized to `[0, 360)`.
 */
export function pointSymbolRotationDeg(id: string, userRotationDeg: number): number {
  const base = pointSymbolGeometryFor(id).baseRotationDeg;
  const total = (base + (Number.isFinite(userRotationDeg) ? userRotationDeg : 0)) % 360;
  return total < 0 ? total + 360 : total;
}

export interface RegularShapeParams {
  readonly points: number;
  /** Radians. Offsets the first vertex so squares sit axis-aligned. */
  readonly angleRad: number;
  /** Inner radius as a fraction of the outer radius; `null` for polygons. */
  readonly radius2Ratio: number | null;
}

/**
 * Polygon parameters for a shape, or `null` for a circle (which both engines
 * draw with a dedicated primitive rather than an n-gon).
 */
export function regularShapeParamsFor(shape: PointSymbolShape): RegularShapeParams | null {
  switch (shape) {
    case 'square':
      return { points: 4, angleRad: Math.PI / 4, radius2Ratio: null };
    case 'diamond':
      return { points: 4, angleRad: 0, radius2Ratio: null };
    case 'triangle':
      return { points: 3, angleRad: 0, radius2Ratio: null };
    case 'star':
      return { points: 5, angleRad: 0, radius2Ratio: 0.4 };
    case 'circle':
    default:
      return null;
  }
}

/**
 * Outline vertices for a shape, centred on the origin, or `null` for a circle.
 * Vertex 0 points up before `angleRad` is applied, matching OpenLayers'
 * `RegularShape`.
 */
export function pointSymbolVertices(
  shape: PointSymbolShape,
  radius: number,
  rotationRad = 0,
): ReadonlyArray<readonly [number, number]> | null {
  const params = regularShapeParamsFor(shape);
  if (!params) return null;
  const inner = params.radius2Ratio == null ? null : radius * params.radius2Ratio;
  const count = inner == null ? params.points : params.points * 2;
  const vertices: Array<readonly [number, number]> = [];
  for (let i = 0; i < count; i += 1) {
    const r = inner == null ? radius : i % 2 === 0 ? radius : inner;
    const angle = params.angleRad + rotationRad - Math.PI / 2 + (i * 2 * Math.PI) / count;
    vertices.push([r * Math.cos(angle), r * Math.sin(angle)]);
  }
  return vertices;
}

/** Trace a symbol outline centred on the current canvas origin. */
export function tracePointSymbolPath(
  ctx: {
    moveTo(x: number, y: number): void;
    lineTo(x: number, y: number): void;
    closePath(): void;
    arc(x: number, y: number, radius: number, start: number, end: number): void;
  },
  shape: PointSymbolShape,
  radius: number,
): void {
  const vertices = pointSymbolVertices(shape, radius);
  if (!vertices) {
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    return;
  }
  vertices.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  });
  ctx.closePath();
}

/**
 * Inline SVG preview of a built-in symbol, drawn with the same vertex math the
 * engines use so a swatch matches what lands on the map (including the id's base
 * rotation). Returns markup for a `size`-square viewBox.
 */
export function pointSymbolPreviewSvg(symbolId: string, size: number, color: string): string {
  const { shape, baseRotationDeg } = pointSymbolGeometryFor(symbolId);
  const radius = size / 2 - 1;
  const centre = size / 2;
  const vertices = pointSymbolVertices(shape, radius, (baseRotationDeg * Math.PI) / 180);
  const body = vertices
    ? `<polygon points="${vertices
        .map(([x, y]) => `${(centre + x).toFixed(2)},${(centre + y).toFixed(2)}`)
        .join(' ')}" fill="${color}" />`
    : `<circle cx="${centre}" cy="${centre}" r="${radius}" fill="${color}" />`;
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" width="${size}" height="${size}">${body}</svg>`;
}

/**
 * Edge length of the bitmap MapLibre icons are rasterized at. Icons are drawn
 * once per shape/colour and scaled with `icon-size`, so changing the point size
 * is a layout-property update rather than a re-upload.
 */
export const POINT_SYMBOL_RASTER_PX = 64;

export interface RasterizedPointSymbol {
  readonly width: number;
  readonly height: number;
  readonly data: Uint8ClampedArray;
}

/**
 * Rasterize a symbol for `map.addImage`. Returns `null` when no 2D canvas is
 * available (SSR, or jsdom without the `canvas` package), which lets callers
 * fall back to MapLibre's built-in circle rendering.
 */
export function rasterizePointSymbol(options: {
  readonly shape: PointSymbolShape;
  readonly fillColor: string;
  readonly strokeColor: string;
  readonly strokeWidth?: number;
}): RasterizedPointSymbol | null {
  if (typeof document === 'undefined') return null;
  const size = POINT_SYMBOL_RASTER_PX;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const strokeWidth = options.strokeWidth ?? Math.max(2, size * 0.05);
  // Inset by the stroke so the outline is not clipped at the bitmap edge.
  const radius = size / 2 - strokeWidth;

  ctx.save();
  ctx.translate(size / 2, size / 2);
  ctx.beginPath();
  tracePointSymbolPath(ctx, options.shape, radius);
  ctx.fillStyle = options.fillColor;
  ctx.fill();
  ctx.lineWidth = strokeWidth;
  ctx.strokeStyle = options.strokeColor;
  ctx.stroke();
  ctx.restore();

  try {
    const image = ctx.getImageData(0, 0, size, size);
    return { width: size, height: size, data: image.data };
  } catch {
    return null;
  }
}

/**
 * True when rotating the symbol is visible to the user. Registry icons are
 * always rotatable; a built-in circle is rotationally symmetric, so the control
 * would do nothing.
 */
export function pointSymbolSupportsRotation(symbolId: string): boolean {
  if (isPointIconId(symbolId)) return true;
  return pointSymbolGeometryFor(symbolId).shape !== 'circle';
}

/**
 * Rasterize an SVG data URL for `map.addImage`. SVG sources have a tiny natural
 * size (Maki is a 15px grid), so they are drawn up to the shared raster size
 * instead of being scaled by the GPU, which would look soft.
 *
 * Resolves `null` when no canvas is available or the SVG fails to decode.
 */
export async function rasterizeSvgPointIcon(
  dataUrl: string,
  sizePx: number = POINT_SYMBOL_RASTER_PX,
): Promise<RasterizedPointSymbol | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  let bitmap: HTMLImageElement;
  try {
    bitmap = await loadImage(dataUrl);
  } catch {
    return null;
  }
  const canvas = document.createElement('canvas');
  canvas.width = sizePx;
  canvas.height = sizePx;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  try {
    // Letterbox non-square artwork so it is never distorted.
    const naturalWidth = bitmap.naturalWidth || sizePx;
    const naturalHeight = bitmap.naturalHeight || sizePx;
    const scale = Math.min(sizePx / naturalWidth, sizePx / naturalHeight);
    const drawWidth = naturalWidth * scale;
    const drawHeight = naturalHeight * scale;
    ctx.drawImage(
      bitmap,
      (sizePx - drawWidth) / 2,
      (sizePx - drawHeight) / 2,
      drawWidth,
      drawHeight,
    );
    const image = ctx.getImageData(0, 0, sizePx, sizePx);
    return { width: sizePx, height: sizePx, data: image.data };
  } catch {
    return null;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`icon failed to load: ${src.slice(0, 64)}`));
    image.src = src;
  });
}
